import { useMemo } from 'react';
import { Box } from '@mui/material';
import { Layout } from 'framework-react-core';
import { Outlet, useMatches, useNavigation } from 'react-router-dom';
import { UserProvider, useUser } from './contexts/UserContext';
import { PageTitleProvider, usePageTitle } from './contexts/PageTitleContext';
import { ROLES } from './redux/api/user.api';
import { useActiveNavigation } from './utilities/useActiveNavigation';
import LoadingScreen from './views/Loading/LoadingScreen';

interface NavLink {
  label: string;
  path: string;
  activePaths: string[];
}

function RootContent() {
  const navigation = useNavigation();
  const isLoading = navigation.state !== 'idle';
  const matches = useMatches();
  const { dynamicTitle } = usePageTitle();
  const { isAuthorized } = useUser();

  const staticTitle =
    [...matches]
      .reverse()
      .map((m) => (m.handle as { pageTitle?: string } | undefined)?.pageTitle)
      .find((t): t is string => typeof t === 'string') ?? 'Rules Engine';

  const pageTitle = dynamicTitle ?? staticTitle;

  const navLinks = useMemo(() => {
    return [
      isAuthorized([ROLES.USER, ROLES.ADMIN, ROLES.VIEW_ONLY])
        ? {
            label: 'Rules',
            path: '/rules',
            activePaths: ['/rules']
          }
        : null,
      isAuthorized([ROLES.ADMIN])
        ? {
            label: 'Project Type',
            path: '/rules/new',
            activePaths: ['/rules/new', '/rules/']
          }
        : null
    ].filter((link): link is NavLink => link !== null);
  }, [isAuthorized]);

  useActiveNavigation(
    navLinks.map(({ label, activePaths }) => ({
      label,
      paths: activePaths
    }))
  );

  return (
    <Layout title={pageTitle} menuOptions={navLinks}>
      <Box
        sx={{
          minHeight: 'calc(100vh - 453px)'
        }}
      >
        {isLoading ? <LoadingScreen /> : <Outlet />}
      </Box>
    </Layout>
  );
}

export function Root() {
  return (
    <UserProvider>
      <PageTitleProvider>
        <RootContent />
      </PageTitleProvider>
    </UserProvider>
  );
}
